import type { Locale } from '../../../../types';
import type { ScopedUtils } from '../../shared/types';
import { buildNumber } from '../../shared/utils/buildNumber';
import type { NumberFormatFunction, NumberSettings, NumberFormatterInstancesMap } from './types';
import { numberFormatterFactories } from './numberFormatterFactories';
import { numberPostProcessors } from './numberPostProcessors';
import { createPostProcessor } from '../../shared/post-processors';
import { buildOverriddenSettings } from './settings-overrides/buildOverriddenSettings';
import { createOverriddenFormattersCache } from './settings-overrides/createOverriddenFormattersCache';


export const createNumberFormatter = (
    locale: Locale,
    settings: NumberSettings,
    scopedUtils: ScopedUtils,
) : NumberFormatFunction => {
    const formatters: NumberFormatterInstancesMap = Object.create(null);

    Object.entries(numberFormatterFactories).forEach(([key, factory]) => {
        formatters[key as keyof typeof numberFormatterFactories] = factory(locale, settings, formatters, scopedUtils);
    });

    const postProcessorsPipe = createPostProcessor(numberPostProcessors, locale);

    const { getOverriddenFormatter } = createOverriddenFormattersCache(locale, formatters, scopedUtils);

    return (
        numberToFormat,
        format,
        options
    ) => {
        let formatter = formatters[format];

        if (!formatter) {
            throw new Error(`Number format not found: ${format}. Please configure Number format for ${format}.`);
        }

        // Настройки вызова могут переопределить дефолтные — тогда нужен динамический форматтер
        const overriddenSettings = buildOverriddenSettings(format, settings, options);
        const _settings = overriddenSettings ?? settings;

        if (overriddenSettings) {
            formatter = getOverriddenFormatter(format, overriddenSettings);
        }

        // regular format processing
        const num = buildNumber(numberToFormat);

        return postProcessorsPipe(formatter.format(num), format, _settings);
    };
}
